import React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/solid";
import moment, { Moment } from "moment-timezone";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { FilterIcon } from "@heroicons/react/outline";
import PopoverComponent from "../popover";
import { useAuth } from "../../context/AuthContext";

type ScheduleCalendarHeaderProps = {
  selectedDate: Moment;
  viewingDate: Moment;
  onChangeViewingDate: (date: Moment) => void;
  setShowFilters?: React.Dispatch<React.SetStateAction<boolean>>;
  filterComponent?: JSX.Element;
};

export default function ScheduleCalendarActionsHeader(
  props: ScheduleCalendarHeaderProps
) {
  let {
    viewingDate,
    onChangeViewingDate,
    filterComponent,
    setShowFilters = () => {},
  } = props;
  const LocalBasePath = "page.tutor.dashboard.schedule";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const { userType }: any = useAuth();

  const onGoToToday = () => {
    onChangeViewingDate(moment());
  };

  const onGoToNextWeek = () => {
    const nextWeekDate = viewingDate
      .locale(i18next.language)
      .clone()
      .day(6)
      .add(1, "days");
    onChangeViewingDate(nextWeekDate);
  };

  const onGoToPreviousWeek = () => {
    const lastWeekDate = viewingDate
      .locale(i18next.language)
      .clone()
      .day(0)
      .subtract(1, "days");
    onChangeViewingDate(lastWeekDate);
  };

  return (
    <header className="relative z-10 border-b border-gray-200 py-4 px-6">
      <div className="flex flex-none items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-900">
          <time dateTime={viewingDate.format("YYYY-MM")}>
            {viewingDate.format("MMMM YYYY")}
          </time>
        </h1>
        <div className="flex items-center">
          {!!filterComponent && (
            <button
              type="button"
              onClick={() => setShowFilters((x) => !x)}
              data-cy="filter"
              className="hidden sm:inline-flex items-center px-4 py-[6px] rtl:ml-4 ltr:mr-4 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
            >
              <div className="flex items-center justify-start gap-2">
                <FilterIcon className="h-4 w-4" aria-hidden="true" />
                {t("filter")}
              </div>
            </button>
          )}
          <div className="flex items-center rounded-md shadow-sm md:items-stretch">
            <button
              onClick={onGoToPreviousWeek}
              type="button"
              data-cy="prevWeek"
              className="flex items-center rtl:rotate-180 justify-center rounded-l-md border border-r-0 border-gray-300 bg-white py-2 pl-3 pr-4 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
            >
              <span className="sr-only">Previous week</span>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              onClick={onGoToToday}
              type="button"
              className="hidden border-t border-b border-gray-300 bg-white px-3.5 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 focus:relative md:block focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-brandblue-500"
            >
              {moment().calendar().split(" ")[0]}
              {/* locale of Today */}
            </button>
            <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
            <button
              onClick={onGoToNextWeek}
              type="button"
              data-cy="nextWeek"
              className="flex items-center rtl:rotate-180 justify-center rounded-r-md border border-l-0 border-gray-300 bg-white py-2 pl-4 pr-3 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
            >
              <span className="sr-only">Next week</span>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <div className="w-9  ltr:ml-4 rtl:mr-4">
              <PopoverComponent
                tabCalendarNumber={userType === "tutor" ? 2 : 1}
              />
            </div>
          </div>
          {/* <div className="hidden ltr:md:ml-4 rtl:md:mr-4 md:flex md:items-center">
            <div className="ltr:ml-4 rtl:mr-4 h-6 w-px bg-gray-300" />
            <button
              type="button"
              className="rounded-full border border-transparent py-2 ltr:ml-4 rtl:mr-4 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
            >
              <span className="sr-only">Open menu</span>
              <DotsVerticalIcon
                className="h-5 w-5 text-gray-400 hover:text-gray-600"
                aria-hidden="true"
              />
            </button>
          </div>
          <Menu as="div" className="relative ltr:ml-6 rtl:mr-6 md:hidden">
            <Menu.Button className="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
              <span className="sr-only">Open menu</span>
              <DotsHorizontalIcon className="h-5 w-5" aria-hidden="true" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-3 w-36 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <a
                        onClick={onGoToToday}
                        className={classNames(
                          active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                          "block px-4 py-2 text-sm"
                        )}
                      >
                        Go to today
                      </a>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu> */}
        </div>
      </div>
      {filterComponent}
    </header>
  );
}
